<template>
    <div>
      <!-- <Header /> -->
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Approval Inbox</h5>
          <p class="page-sub">
            <router-link :to="$url('/approvals')" class="breadcrumb-link">Approvals</router-link>
            <span class="breadcrumb-sep">/</span>
            <span>My Inbox</span>
          </p>
        </div>
      </div>
  
      <!-- Header card -->
      <div class="inbox-header-card">
        <div class="inbox-header-left">
          <div class="inbox-shield-icon"><font-awesome-icon icon="shield-alt" /></div>
          <div>
            <h2 class="inbox-title">My Approval Inbox</h2>
            <p class="inbox-sub">Requests assigned to your designation for review and action.</p>
          </div>
        </div>
        <div class="stat-pills">
          <div class="stat-pill">
            <p class="stat-pill-label">Total</p>
            <p class="stat-pill-val">{{ stats.total }}</p>
          </div>
          <div class="stat-pill stat-pill--blue">
            <p class="stat-pill-label">Pending</p>
            <p class="stat-pill-val stat-pill-val--blue">{{ stats.pending }}</p>
          </div>
          <div v-if="stats.overdue > 0" class="stat-pill stat-pill--red">
            <p class="stat-pill-label">Overdue</p>
            <p class="stat-pill-val stat-pill-val--red">{{ stats.overdue }}</p>
          </div>
        </div>
      </div>
  
      <!-- Filters -->
      <div class="filters-row">
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input"
            placeholder="Search by name, reference, workflow..."
            v-model="search" />
        </div>
        <select class="filter-select" v-model="moduleFilter" @change="fetchItems">
          <option value="all">All Modules</option>
          <option v-for="(m, key) in MODULE_META" :key="key" :value="key">{{ m.label }}</option>
        </select>
        <select class="filter-select" v-model="statusFilter" @change="fetchItems">
          <option value="all">All Statuses</option>
          <option value="in_progress">In Progress</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
      </div>
  
      <!-- Empty -->
      <div v-else-if="filtered.length === 0" class="empty-state">
        <div class="empty-icon-wrap"><font-awesome-icon icon="shield-alt" /></div>
        <h3 class="empty-title">No approvals found</h3>
        <p class="empty-sub">
          {{ hasActiveFilters
            ? 'No requests match your current filters.'
            : 'You have no pending approvals. Check back later.' }}
        </p>
        <button v-if="hasActiveFilters" class="btn-clear-filters" @click="clearFilters">
          Clear filters
        </button>
      </div>
  
      <!-- Request list -->
      <div v-else class="request-list">
        <p class="result-count">Showing {{ filtered.length }} request{{ filtered.length !== 1 ? 's' : '' }}</p>
  
        <div v-for="item in filtered" :key="reqOf(item).reference" class="request-card">
          <!-- Card top -->
          <div class="request-card-body">
            <div class="request-card-left">
              <!-- Module icon -->
              <div class="module-icon" :style="moduleStyle(reqOf(item).module)">
                <font-awesome-icon :icon="moduleMeta(reqOf(item).module).icon" />
              </div>
  
              <div class="request-info">
                <!-- Title row -->
                <div class="request-title-row">
                  <span class="request-name">{{ reqOf(item).workflow && reqOf(item).workflow.name || moduleMeta(reqOf(item).module).label + ' Approval' }}</span>
                  <span class="status-badge" :class="statusClass(reqOf(item).status)">
                    {{ reqOf(item).status.replace('_', ' ') }}
                  </span>
                  <span v-if="isOverdue(reqOf(item))" class="overdue-badge">
                    <font-awesome-icon icon="clock" class="me-1" /> Overdue
                  </span>
                </div>
  
                <!-- Meta row -->
                <div class="request-meta-row">
                  <span class="meta-item">
                    <font-awesome-icon icon="user-tie" class="meta-icon" />
                    {{ requesterName(reqOf(item).requested_by) }}
                    <span v-if="reqOf(item).requested_by && reqOf(item).requested_by.employee && reqOf(item).requested_by.employee.job_title" class="meta-dim">
                      · {{ reqOf(item).requested_by.employee.job_title.name }}
                    </span>
                  </span>
                  <span class="meta-item">
                    <font-awesome-icon icon="calendar-alt" class="meta-icon" />
                    {{ fmtDate(reqOf(item).submitted_at) }}
                  </span>
                  <span class="ref-chip">{{ reqOf(item).module_reference }}</span>
                </div>
  
                <!-- Step progress dots -->
                <div v-if="reqOf(item).steps && reqOf(item).steps.length" class="step-progress">
                  <template v-for="(step, i) in reqOf(item).steps" :key="step.id">
                    <div class="step-dot"
                      :class="{
                        'step-dot--approved':  step.status === 'approved',
                        'step-dot--rejected':  step.status === 'rejected',
                        'step-dot--active':    step.status === 'in_progress',
                        'step-dot--pending':   step.status !== 'approved' && step.status !== 'rejected' && step.status !== 'in_progress',
                      }">
                      <span v-if="step.status === 'approved'">&#10003;</span>
                      <span v-else-if="step.status === 'rejected'">&#10007;</span>
                      <span v-else>{{ step.step_order }}</span>
                    </div>
                    <span class="step-label hidden-sm">{{ step.designation_name }}</span>
                    <div v-if="i < reqOf(item).steps.length - 1"
                      class="step-connector"
                      :class="step.status === 'approved' ? 'step-connector--done' : 'step-connector--pending'">
                    </div>
                  </template>
                </div>
  
                <p v-if="reqOf(item).notes" class="request-notes">"{{ reqOf(item).notes }}"</p>
              </div>
            </div>
  
            <!-- Right actions -->
            <div class="request-card-right">
              <div v-if="isActable(reqOf(item))" class="action-btns">
                <button class="btn-approve" @click="openAction(reqOf(item), 'approve')">
                  <font-awesome-icon icon="check" class="me-1" /> Approve
                </button>
                <button class="btn-reject" @click="openAction(reqOf(item), 'reject')">
                  <font-awesome-icon icon="times" class="me-1" /> Reject
                </button>
                <button class="btn-reassign" @click="openReassign(reqOf(item))" title="Reassign step to another employee">
                  <font-awesome-icon icon="user-edit" />
                </button>
              </div>
              <span v-if="currentStepDue(reqOf(item))"
                class="due-label"
                :class="isOverdue(reqOf(item)) ? 'due-label--red' : 'due-label--gray'">
                Due: {{ fmtDate(currentStepDue(reqOf(item))) }}
              </span>
              <div class="card-footer-btns">
                <button class="btn-preview" @click="openPreview(reqOf(item))">
                  <font-awesome-icon icon="file-alt" class="me-1" /> Preview
                </button>
                <button class="btn-toggle-details"
                  @click="toggleExpanded(reqOf(item).reference)">
                  <font-awesome-icon icon="eye" class="me-1" />
                  {{ expandedRef === reqOf(item).reference ? 'Hide' : 'Details' }}
                  <font-awesome-icon :icon="expandedRef === reqOf(item).reference ? 'chevron-up' : 'chevron-down'" class="ms-1" />
                </button>
              </div>
            </div>
          </div>
  
          <!-- Expanded step history -->
          <div v-if="expandedRef === reqOf(item).reference" class="step-history">
            <p class="step-history-title">Step History</p>
            <div class="step-history-list">
              <div v-for="step in reqOf(item).steps" :key="step.id"
                class="step-history-row"
                :class="{ 'step-history-row--active': step.step_order === reqOf(item).current_step }">
                <div class="step-dot step-dot--lg"
                  :class="{
                    'step-dot--approved': step.status === 'approved',
                    'step-dot--rejected': step.status === 'rejected',
                    'step-dot--active':   step.status === 'in_progress',
                    'step-dot--pending':  step.status !== 'approved' && step.status !== 'rejected' && step.status !== 'in_progress',
                  }">
                  <span v-if="step.status === 'approved'">&#10003;</span>
                  <span v-else-if="step.status === 'rejected'">&#10007;</span>
                  <span v-else>{{ step.step_order }}</span>
                </div>
                <div class="step-history-info">
                  <div class="step-history-title-row">
                    <span class="step-history-name">{{ step.designation_name }}</span>
                    <span class="status-badge status-badge--sm" :class="statusClass(step.status)">
                      {{ step.status.replace('_', ' ') }}
                    </span>
                  </div>
                  <p v-if="step.acted_by" class="step-history-meta">
                    Actioned by <strong>{{ step.acted_by.name }}</strong>
                    <span v-if="step.acted_at"> · {{ fmtDate(step.acted_at) }} {{ fmtTime(step.acted_at) }}</span>
                  </p>
                  <p v-if="step.comments" class="step-history-comment">"{{ step.comments }}"</p>
                  <p v-if="step.due_at && !step.acted_at" class="step-history-due"
                    :class="new Date(step.due_at) < new Date() ? 'text-red' : 'text-gray'">
                    Due: {{ fmtDate(step.due_at) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           PREVIEW MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay preview-overlay" :class="{ open: !!previewTarget }"
        @click.self="previewTarget = null">
        <div class="modal-box preview-box" v-if="previewTarget">
          <!-- Header -->
          <div class="preview-hd" :style="{ background: moduleMeta(previewTarget.module).color }">
            <div class="preview-hd-inner">
              <div class="preview-hd-icon">
                <font-awesome-icon :icon="moduleMeta(previewTarget.module).icon" />
              </div>
              <div>
                <h3 class="preview-hd-title">{{ moduleMeta(previewTarget.module).label }} — Preview</h3>
                <p class="preview-hd-sub">{{ previewTarget.module_reference }}</p>
              </div>
            </div>
            <button class="modal-close-btn-light" @click="previewTarget = null">
              <font-awesome-icon icon="times" />
            </button>
          </div>

          <!-- Loading -->
          <div v-if="previewLoading" class="preview-loading">
            <div class="spinner-border text-primary" style="width:2rem;height:2rem;" role="status"></div>
            <p class="preview-loading-text">Loading document...</p>
          </div>

          <!-- Content -->
          <div v-else class="preview-body">

            <!-- Submission info row -->
            <div class="preview-meta-bar">
              <span class="preview-meta-item">
                <font-awesome-icon icon="user-tie" class="me-1" />
                Submitted by <strong>{{ previewRequesterName || requesterName(previewTarget.requested_by) }}</strong>
                <span v-if="previewTarget.requested_by?.employee?.job_title" class="meta-dim"> · {{ previewTarget.requested_by.employee.job_title.name }}</span>
              </span>
              <span class="preview-meta-item">
                <font-awesome-icon icon="calendar-alt" class="me-1" />
                {{ fmtDate(previewTarget.submitted_at) }}
              </span>
              <span class="status-badge" :class="statusClass(previewTarget.status)">{{ previewTarget.status.replace('_',' ') }}</span>
            </div>
            <p v-if="previewTarget.notes" class="preview-notes">"{{ previewTarget.notes }}"</p>

            <!-- LEAVE -->
            <template v-if="previewDoc && previewTarget.module === 'leave'">
              <p class="preview-section-title">Leave Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Employee</p>
                  <p class="pf-val">{{ previewDoc.employee?.first_name }} {{ previewDoc.employee?.last_name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Leave Type</p>
                  <p class="pf-val">
                    <span v-if="previewDoc.leave_type" class="leave-type-chip"
                      :style="{ background: (previewDoc.leave_type.color || '#e0e7ef') + '22', color: previewDoc.leave_type.color || '#1a2b45', border: '1px solid ' + (previewDoc.leave_type.color || '#e0e7ef') + '55' }">
                      {{ previewDoc.leave_type.name }}
                    </span>
                    <span v-else>—</span>
                  </p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">From</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.from_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">To</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.to_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Days</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.days }} day{{ previewDoc.days !== 1 ? 's' : '' }}</p>
                </div>
                <div v-if="previewDoc.employee?.position" class="preview-field">
                  <p class="pf-label">Position</p>
                  <p class="pf-val">{{ previewDoc.employee.position.name }}</p>
                </div>
              </div>
              <div v-if="previewDoc.reason" class="preview-reason-box">
                <p class="pf-label">Reason</p>
                <p class="pf-reason-text">{{ previewDoc.reason }}</p>
              </div>
              <div v-if="previewDoc.attachment" class="preview-field" style="margin-top:10px;">
                <p class="pf-label">Attachment</p>
                <a :href="previewDoc.attachment" target="_blank" class="preview-attach-link">
                  <font-awesome-icon icon="paperclip" class="me-1" /> View Attachment
                </a>
              </div>
            </template>

            <!-- LOAN -->
            <template v-else-if="previewDoc && previewTarget.module === 'loan'">
              <p class="preview-section-title">Loan Application Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Employee</p>
                  <p class="pf-val">{{ previewDoc.employee?.first_name }} {{ previewDoc.employee?.last_name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Loan Type</p>
                  <p class="pf-val">{{ previewDoc.loan_type?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Amount Requested</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.amount_requested) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Repayment Type</p>
                  <p class="pf-val">{{ previewDoc.repayment_type?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Installments</p>
                  <p class="pf-val">{{ previewDoc.installments_total }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Monthly EMI</p>
                  <p class="pf-val">{{ fmtMoney(previewDoc.emi_amount) }}</p>
                </div>
              </div>
              <div v-if="previewDoc.notes" class="preview-reason-box">
                <p class="pf-label">Notes</p>
                <p class="pf-reason-text">{{ previewDoc.notes }}</p>
              </div>
            </template>

            <!-- PAYROLL -->
            <template v-else-if="previewDoc && previewTarget.module === 'payroll'">
              <p class="preview-section-title">Payroll Period Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Period Name</p>
                  <p class="pf-val">{{ previewDoc.name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Start Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.start_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">End Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.end_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Reference</p>
                  <p class="pf-val"><span class="ref-chip">{{ previewDoc.reference }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Gross Pay</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.total_gross) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Net Pay</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.total_net) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Total Tax</p>
                  <p class="pf-val">{{ fmtMoney(previewDoc.total_tax) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Total Deductions</p>
                  <p class="pf-val">{{ fmtMoney(previewDoc.total_deduction) }}</p>
                </div>
              </div>
            </template>

            <!-- EXPENSE -->
            <template v-else-if="previewDoc && previewTarget.module === 'expense'">
              <p class="preview-section-title">Expense Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Title</p>
                  <p class="pf-val">{{ previewDoc.title }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Amount</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.amount) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.transaction_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Payment Type</p>
                  <p class="pf-val">{{ previewDoc.payment_type?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Account</p>
                  <p class="pf-val">{{ previewDoc.ledger?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Store</p>
                  <p class="pf-val">{{ previewDoc.store?.name || '—' }}</p>
                </div>
              </div>
              <div v-if="previewDoc.description" class="preview-reason-box">
                <p class="pf-label">Description</p>
                <p class="pf-reason-text">{{ previewDoc.description }}</p>
              </div>
            </template>

            <!-- JOB REQUISITION -->
            <template v-else-if="previewDoc && previewTarget.module === 'job-requisition'">
              <p class="preview-section-title">Job Requisition Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Position</p>
                  <p class="pf-val">{{ previewDoc.job_position?.name || previewDoc.position }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Department</p>
                  <p class="pf-val">{{ previewDoc.department?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Employment Type</p>
                  <p class="pf-val">{{ previewDoc.employment_type }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Vacancies</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.position_count }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Location</p>
                  <p class="pf-val">{{ previewDoc.company_location?.name || previewDoc.location || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Pay Grade</p>
                  <p class="pf-val">{{ previewDoc.pay_grade?.name || '—' }}</p>
                </div>
                <div v-if="previewDoc.proposed_start_date" class="preview-field">
                  <p class="pf-label">Proposed Start</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.proposed_start_date) }}</p>
                </div>
              </div>
              <div v-if="previewDoc.justification" class="preview-reason-box">
                <p class="pf-label">Justification</p>
                <p class="pf-reason-text">{{ previewDoc.justification }}</p>
              </div>
              <div v-if="previewDoc.job_description" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Job Description</p>
                <p class="pf-reason-text preview-clamp">{{ previewDoc.job_description }}</p>
              </div>
              <div v-if="previewDoc.requirements" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Requirements</p>
                <p class="pf-reason-text preview-clamp">{{ previewDoc.requirements }}</p>
              </div>
            </template>

            <!-- WORKFORCE PLAN -->
            <template v-else-if="previewDoc && previewTarget.module === 'workforce-plan'">
              <p class="preview-section-title">Workforce Plan Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Plan Name</p>
                  <p class="pf-val">{{ previewDoc.name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Department</p>
                  <p class="pf-val">{{ previewDoc.department?.name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Period</p>
                  <p class="pf-val">{{ previewDoc.period_type }} {{ previewDoc.year }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Category</p>
                  <p class="pf-val">{{ previewDoc.category }}</p>
                </div>
                <div v-if="previewDoc.total_headcount" class="preview-field">
                  <p class="pf-label">Total Headcount</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.total_headcount }}</p>
                </div>
                <div v-if="previewDoc.estimated_budget" class="preview-field">
                  <p class="pf-label">Estimated Budget</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.estimated_budget) }}</p>
                </div>
              </div>
              <div v-if="previewDoc.business_need" class="preview-reason-box">
                <p class="pf-label">Business Need</p>
                <p class="pf-reason-text">{{ previewDoc.business_need }}</p>
              </div>
            </template>

            <!-- JOURNAL -->
            <template v-else-if="previewDoc && previewTarget.module === 'journal'">
              <p class="preview-section-title">Journal Posting Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Reference</p>
                  <p class="pf-val"><span class="jnl-ref-chip">{{ previewDoc.reference_no }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Amount</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.amount) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Status</p>
                  <p class="pf-val">
                    <span class="status-badge" :class="previewDoc.status === 'posted' ? 'status-badge--green' : 'status-badge--gray'">
                      {{ previewDoc.status }}
                    </span>
                  </p>
                </div>
              </div>
              <div v-if="previewDoc.description" class="preview-reason-box">
                <p class="pf-label">Description</p>
                <p class="pf-reason-text">{{ previewDoc.description }}</p>
              </div>
              <div v-if="previewDoc.entries && previewDoc.entries.length" style="margin-top:4px;">
                <p class="preview-section-title" style="margin-bottom:8px;">Journal Entries</p>
                <div class="jnl-entries-table">
                  <div class="jnl-entries-head">
                    <span>Account</span>
                    <span class="text-right">Debit</span>
                    <span class="text-right">Credit</span>
                  </div>
                  <div v-for="(entry, i) in previewDoc.entries" :key="i" class="jnl-entries-row">
                    <span class="jnl-account-name">{{ entry.account ? entry.account.name : '—' }}</span>
                    <span class="text-right jnl-debit">{{ entry.debit > 0 ? fmtMoney(entry.debit) : '—' }}</span>
                    <span class="text-right jnl-credit">{{ entry.credit > 0 ? fmtMoney(entry.credit) : '—' }}</span>
                  </div>
                  <div class="jnl-entries-total">
                    <span>Total</span>
                    <span class="text-right">{{ fmtMoney(previewDoc.entries.reduce((s, e) => s + Number(e.debit || 0), 0)) }}</span>
                    <span class="text-right">{{ fmtMoney(previewDoc.entries.reduce((s, e) => s + Number(e.credit || 0), 0)) }}</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- PURCHASE ORDER -->
            <template v-else-if="previewDoc && previewTarget.module === 'purchase'">
              <p class="preview-section-title">Purchase Order Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Batch Code</p>
                  <p class="pf-val"><span class="ref-chip">{{ previewDoc.batch_code || '—' }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Supplier</p>
                  <p class="pf-val">{{ previewDoc.supplier?.supplier_name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Store</p>
                  <p class="pf-val">{{ previewDoc.store?.store_name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Total Amount</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.purchase_item?.reduce((s, i) => s + (Number(i.cost_price || 0) * Number(i.quantity || 0)), 0)) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Items</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.purchase_item?.length || 0 }} line item{{ (previewDoc.purchase_item?.length || 0) !== 1 ? 's' : '' }}</p>
                </div>
              </div>
              <div v-if="previewDoc.purchase_item && previewDoc.purchase_item.length" style="margin-top:4px;">
                <p class="preview-section-title" style="margin-bottom:8px;">Line Items</p>
                <div class="jnl-entries-table">
                  <div class="jnl-entries-head" style="grid-template-columns:1fr 80px 110px 110px;">
                    <span>Product</span>
                    <span class="text-right">Qty</span>
                    <span class="text-right">Unit Cost</span>
                    <span class="text-right">Subtotal</span>
                  </div>
                  <div v-for="(item, i) in previewDoc.purchase_item" :key="i"
                    class="jnl-entries-row" style="grid-template-columns:1fr 80px 110px 110px;">
                    <span class="jnl-account-name">
                      {{ item.product?.product_name || '—' }}
                      <span v-if="item.variant" class="meta-dim"> · {{ item.variant.name }}</span>
                    </span>
                    <span class="text-right">{{ item.quantity }}</span>
                    <span class="text-right">{{ fmtMoney(item.cost_price) }}</span>
                    <span class="text-right jnl-debit">{{ fmtMoney(Number(item.cost_price || 0) * Number(item.quantity || 0)) }}</span>
                  </div>
                  <div class="jnl-entries-total" style="grid-template-columns:1fr 80px 110px 110px;">
                    <span>Total</span>
                    <span class="text-right">{{ previewDoc.purchase_item.reduce((s, i) => s + Number(i.quantity || 0), 0) }}</span>
                    <span></span>
                    <span class="text-right">{{ fmtMoney(previewDoc.purchase_item.reduce((s, i) => s + (Number(i.cost_price || 0) * Number(i.quantity || 0)), 0)) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="previewDoc.note" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Note</p>
                <p class="pf-reason-text">{{ previewDoc.note }}</p>
              </div>
            </template>

            <!-- JOB OFFER -->
            <template v-else-if="previewDoc && previewTarget.module === 'job-offer'">
              <p class="preview-section-title">Offer Letter Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Candidate</p>
                  <p class="pf-val">{{ previewDoc.candidate ? previewDoc.candidate.first_name + ' ' + previewDoc.candidate.last_name : '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Position</p>
                  <p class="pf-val">{{ previewDoc.position || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Employment Type</p>
                  <p class="pf-val">{{ previewDoc.employment_type || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Salary</p>
                  <p class="pf-val pf-highlight">{{ fmtMoney(previewDoc.salary) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Start Date</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.start_date) }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Candidate Email</p>
                  <p class="pf-val">{{ previewDoc.candidate?.email || '—' }}</p>
                </div>
              </div>
              <div v-if="previewDoc.benefits" class="preview-reason-box" style="margin-top:4px;">
                <p class="pf-label">Benefits</p>
                <p class="pf-reason-text preview-clamp">{{ previewDoc.benefits }}</p>
              </div>
              <div v-if="previewDoc.additional_terms" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Additional Terms</p>
                <p class="pf-reason-text preview-clamp">{{ previewDoc.additional_terms }}</p>
              </div>
              <div v-if="previewDoc.offer_body" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Offer Body</p>
                <p class="pf-reason-text preview-clamp">{{ previewDoc.offer_body }}</p>
              </div>
            </template>

            <!-- STOCK REQUISITION (INTERNAL ISSUE) -->
            <template v-else-if="previewDoc && previewTarget.module === 'internal-issue'">
              <p class="preview-section-title">Requisition Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Reference</p>
                  <p class="pf-val"><span class="jnl-ref-chip">{{ previewDoc.batch_code || '—' }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Date</p>
                  <p class="pf-val">{{ previewDoc.date || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Store</p>
                  <p class="pf-val">{{ previewDoc.store?.store_name || '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">{{ previewDoc.requisition_for === 'individual' ? 'Individual' : 'Department' }}</p>
                  <p class="pf-val">{{ previewDoc.department || '—' }}</p>
                </div>
                <div class="preview-field" v-if="previewDoc.unit">
                  <p class="pf-label">Unit</p>
                  <p class="pf-val">{{ previewDoc.unit }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Items</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.issue_items?.length || 0 }} product{{ (previewDoc.issue_items?.length || 0) !== 1 ? 's' : '' }}</p>
                </div>
              </div>
              <div class="preview-reason-box">
                <p class="pf-label">Purpose</p>
                <p class="pf-reason-text">{{ previewDoc.purpose || '—' }}</p>
              </div>
              <div v-if="previewDoc.notes" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Notes</p>
                <p class="pf-reason-text">{{ previewDoc.notes }}</p>
              </div>
              <div v-if="previewDoc.issue_items && previewDoc.issue_items.length" style="margin-top:4px;">
                <p class="preview-section-title" style="margin-bottom:8px;">Items Requested</p>
                <div class="jnl-entries-table">
                  <div class="jnl-entries-head" style="grid-template-columns:1fr 120px 80px;">
                    <span>Product</span>
                    <span>Variant</span>
                    <span class="text-right">Qty</span>
                  </div>
                  <div v-for="(item, i) in previewDoc.issue_items" :key="i"
                    class="jnl-entries-row" style="grid-template-columns:1fr 120px 80px;">
                    <span class="jnl-account-name">{{ item.product?.product_name || '—' }}</span>
                    <span class="meta-dim">{{ item.variant?.variant_name || '—' }}</span>
                    <span class="text-right jnl-debit">{{ item.quantity }}</span>
                  </div>
                  <div class="jnl-entries-total" style="grid-template-columns:1fr 120px 80px;">
                    <span>Total</span>
                    <span></span>
                    <span class="text-right">{{ previewDoc.issue_items.reduce((s, i) => s + Number(i.quantity || 0), 0) }}</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- VENDOR REGISTRATION -->
            <template v-else-if="previewDoc && previewTarget.module === 'vendor'">
              <p class="preview-section-title">Vendor Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Vendor Name</p>
                  <p class="pf-val">{{ previewDoc.name }}</p>
                </div>
                <div v-if="previewDoc.trading_name" class="preview-field">
                  <p class="pf-label">Trading Name</p>
                  <p class="pf-val">{{ previewDoc.trading_name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Vendor No.</p>
                  <p class="pf-val"><span class="ref-chip">{{ previewDoc.vendor_number || '—' }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Email</p>
                  <p class="pf-val">{{ previewDoc.email || '—' }}</p>
                </div>
                <div v-if="previewDoc.phone" class="preview-field">
                  <p class="pf-label">Phone</p>
                  <p class="pf-val">{{ previewDoc.phone }}</p>
                </div>
                <div v-if="previewDoc.website" class="preview-field">
                  <p class="pf-label">Website</p>
                  <p class="pf-val">{{ previewDoc.website }}</p>
                </div>
                <div v-if="previewDoc.rc_number" class="preview-field">
                  <p class="pf-label">RC Number</p>
                  <p class="pf-val">{{ previewDoc.rc_number }}</p>
                </div>
                <div v-if="previewDoc.tax_id" class="preview-field">
                  <p class="pf-label">Tax ID</p>
                  <p class="pf-val">{{ previewDoc.tax_id }}</p>
                </div>
                <div v-if="previewDoc.country" class="preview-field">
                  <p class="pf-label">Country</p>
                  <p class="pf-val">{{ previewDoc.country }}</p>
                </div>
                <div v-if="previewDoc.state || previewDoc.city" class="preview-field">
                  <p class="pf-label">Location</p>
                  <p class="pf-val">{{ [previewDoc.city, previewDoc.state].filter(Boolean).join(', ') || '—' }}</p>
                </div>
                <div v-if="previewDoc.risk_rating" class="preview-field">
                  <p class="pf-label">Risk Rating</p>
                  <p class="pf-val" :class="previewDoc.risk_rating === 'high' ? 'pf-val--red' : previewDoc.risk_rating === 'medium' ? 'pf-val--amber' : ''">
                    {{ previewDoc.risk_rating.charAt(0).toUpperCase() + previewDoc.risk_rating.slice(1) }}
                  </p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Status</p>
                  <p class="pf-val">
                    <span class="status-badge" :class="statusClass(previewDoc.approval_status || previewDoc.status)">
                      {{ (previewDoc.approval_status || previewDoc.status || '—').replace('_', ' ') }}
                    </span>
                  </p>
                </div>
              </div>

              <!-- Primary Contact -->
              <template v-if="previewDoc.primary_contact">
                <p class="preview-section-title" style="margin-top:4px;">Primary Contact</p>
                <div class="preview-grid">
                  <div class="preview-field">
                    <p class="pf-label">Name</p>
                    <p class="pf-val">{{ previewDoc.primary_contact.name }}</p>
                  </div>
                  <div v-if="previewDoc.primary_contact.role" class="preview-field">
                    <p class="pf-label">Role</p>
                    <p class="pf-val">{{ previewDoc.primary_contact.role }}</p>
                  </div>
                  <div v-if="previewDoc.primary_contact.email" class="preview-field">
                    <p class="pf-label">Email</p>
                    <p class="pf-val">{{ previewDoc.primary_contact.email }}</p>
                  </div>
                  <div v-if="previewDoc.primary_contact.phone" class="preview-field">
                    <p class="pf-label">Phone</p>
                    <p class="pf-val">{{ previewDoc.primary_contact.phone }}</p>
                  </div>
                </div>
              </template>

              <!-- Categories -->
              <template v-if="previewDoc.categories && previewDoc.categories.length">
                <p class="preview-section-title" style="margin-top:4px;">Categories</p>
                <div class="vendor-cats">
                  <span v-for="cat in previewDoc.categories" :key="cat.id" class="vendor-cat-chip">
                    {{ cat.name }}
                  </span>
                </div>
              </template>

              <div v-if="previewDoc.address" class="preview-reason-box">
                <p class="pf-label">Address</p>
                <p class="pf-reason-text">{{ previewDoc.address }}</p>
              </div>
              <div v-if="previewDoc.notes" class="preview-reason-box" style="margin-top:8px;">
                <p class="pf-label">Notes</p>
                <p class="pf-reason-text">{{ previewDoc.notes }}</p>
              </div>
            </template>

            <!-- PURCHASE REQUISITION -->
            <template v-else-if="previewDoc && previewTarget.module === 'purchase_requisition'">
              <div class="pr-preview-meta">
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">PR No.</span>
                  <span class="ref-chip">{{ previewDoc.pr_number || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Requester</span>
                  <span class="pr-meta-val">{{ previewDoc.requester ? previewDoc.requester.first_name + ' ' + previewDoc.requester.last_name : '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Category</span>
                  <span class="pr-meta-val">{{ previewDoc.category?.name || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Location</span>
                  <span class="pr-meta-val">{{ previewDoc.location?.name || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Department</span>
                  <span class="pr-meta-val">{{ previewDoc.department?.name || '—' }}</span>
                </div>
                <div class="pr-meta-chip pr-meta-chip--accent">
                  <span class="pr-meta-label">Total Value</span>
                  <span class="pr-meta-val pr-meta-total">{{ fmtMoney(previewDoc.lines?.reduce((s, l) => s + Number(l.estimated_total || 0), 0)) }}</span>
                </div>
              </div>

              <div v-if="previewDoc.lines && previewDoc.lines.length" class="pr-lines-wrap">
                <div class="pr-lines-head">
                  Line Items <span class="pr-lines-count">{{ previewDoc.lines.length }}</span>
                </div>
                <table class="pr-lines-table">
                  <colgroup>
                    <col class="prc-desc" />
                    <col class="prc-qty" />
                    <col class="prc-cost" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th class="pr-th-r">Qty</th>
                      <th class="pr-th-r">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, i) in previewDoc.lines" :key="line.id" :class="i % 2 === 1 ? 'pr-row-alt' : ''">
                      <td class="pr-td-desc">{{ line.description || '—' }}</td>
                      <td class="pr-td-r pr-td-qty">{{ line.quantity }}</td>
                      <td class="pr-td-r pr-td-cost">{{ fmtMoney(Number(line.estimated_total || 0)) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="2" class="pr-tfoot-label">Estimated Total</td>
                      <td class="pr-tfoot-val">{{ fmtMoney(previewDoc.lines.reduce((s, l) => s + Number(l.estimated_total || 0), 0)) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div v-if="previewDoc.notes" class="preview-reason-box">
                <p class="pf-label">Notes</p>
                <p class="pf-reason-text">{{ previewDoc.notes }}</p>
              </div>
            </template>

            <!-- PROCUREMENT PURCHASE ORDER -->
            <template v-else-if="previewDoc && previewTarget.module === 'purchase_order'">
              <!-- Meta chips row 1: PO identity -->
              <div class="pr-preview-meta">
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">PO No.</span>
                  <span class="ref-chip">{{ previewDoc.po_number || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Vendor</span>
                  <span class="pr-meta-val po-vendor-val">{{ previewDoc.vendor?.name || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Currency</span>
                  <span class="pr-meta-val">{{ previewDoc.currency || '—' }}</span>
                </div>
                <div class="pr-meta-chip">
                  <span class="pr-meta-label">Payment Terms</span>
                  <span class="pr-meta-val">{{ previewDoc.payment_terms || '—' }}</span>
                </div>
                <div class="pr-meta-chip" v-if="previewDoc.expected_delivery_date">
                  <span class="pr-meta-label">Expected Delivery</span>
                  <span class="pr-meta-val">{{ fmtDate(previewDoc.expected_delivery_date) }}</span>
                </div>
                <div class="pr-meta-chip pr-meta-chip--accent">
                  <span class="pr-meta-label">Order Total</span>
                  <span class="pr-meta-val pr-meta-total">{{ previewDoc.currency }} {{ fmtMoney(previewDoc.total) }}</span>
                </div>
              </div>

              <!-- Line items -->
              <div v-if="poPreviewLines.length > 0" class="pr-lines-wrap">
                <div class="pr-lines-head">
                  Line Items <span class="pr-lines-count">{{ poPreviewLines.length }}</span>
                  <span v-if="isActable(previewTarget) && canAmendPO" class="po-amend-badge">
                    <font-awesome-icon icon="edit" /> Quantities editable
                  </span>
                </div>
                <table class="pr-lines-table po-lines-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th class="pr-th-r">Qty</th>
                      <th class="pr-th-r">Unit Price</th>
                      <th class="pr-th-r">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, i) in poPreviewLines" :key="line.id || i" :class="i % 2 === 1 ? 'pr-row-alt' : ''">
                      <td class="pr-td-desc">
                        {{ line.description || '—' }}
                        <span v-if="line.specification" class="po-line-spec">{{ line.specification }}</span>
                      </td>
                      <td class="pr-td-r pr-td-qty">
                        <template v-if="isActable(previewTarget) && canAmendPO">
                          <input
                            type="number"
                            class="amend-qty-input"
                            :value="amendQtys[line.id]"
                            @input="setAmendQty(line.id, $event.target.value)"
                            min="0.001"
                            step="any"
                          />
                          <span v-if="line.uom" class="po-line-uom"> {{ line.uom }}</span>
                        </template>
                        <template v-else>
                          {{ line.quantity }}<span v-if="line.uom" class="po-line-uom"> {{ line.uom }}</span>
                        </template>
                      </td>
                      <td class="pr-td-r">{{ fmtMoney(Number(line.unit_price || 0)) }}</td>
                      <td class="pr-td-r pr-td-cost">
                        {{ fmtMoney(Number(amendQtys[line.id] || line.quantity) * Number(line.unit_price || 0)) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr v-if="previewDoc.tax_amount">
                      <td colspan="3" class="pr-tfoot-label">Subtotal</td>
                      <td class="pr-tfoot-val">{{ fmtMoney(poPreviewLines.reduce((s, l) => s + Number(amendQtys[l.id] || l.quantity) * Number(l.unit_price || 0), 0)) }}</td>
                    </tr>
                    <tr v-if="previewDoc.tax_amount">
                      <td colspan="3" class="pr-tfoot-label">Tax / VAT</td>
                      <td class="pr-tfoot-val">{{ fmtMoney(previewDoc.tax_amount) }}</td>
                    </tr>
                    <tr class="po-tfoot-total">
                      <td colspan="3" class="pr-tfoot-label po-tfoot-total-label">Total</td>
                      <td class="pr-tfoot-val po-tfoot-total-val">
                        {{ fmtMoney(poPreviewLines.reduce((s, l) => s + Number(amendQtys[l.id] || l.quantity) * Number(l.unit_price || 0), 0) + Number(previewDoc.tax_amount || 0)) }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <!-- Delivery address -->
              <div v-if="previewDoc.delivery_address" class="po-preview-addr-block">
                <p class="po-preview-addr-label">Delivery Address</p>
                <p class="po-preview-addr-val">{{ previewDoc.delivery_address }}</p>
              </div>

              <!-- Notes / instructions -->
              <div v-if="previewDoc.description || previewDoc.notes" class="preview-reason-box" style="margin-top:10px;">
                <p v-if="previewDoc.description" class="pf-label">Instructions</p>
                <p v-if="previewDoc.description" class="pf-reason-text">{{ previewDoc.description }}</p>
                <p v-if="previewDoc.notes" class="pf-label" :style="previewDoc.description ? 'margin-top:8px' : ''">Internal Notes</p>
                <p v-if="previewDoc.notes" class="pf-reason-text">{{ previewDoc.notes }}</p>
              </div>
            </template>

            <!-- CRM CAMPAIGN -->
            <template v-else-if="previewDoc && previewTarget.module === 'crm_campaign'">
              <p class="preview-section-title">Campaign Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Campaign No.</p>
                  <p class="pf-val"><span class="ref-chip">{{ previewDoc.campaign_number || '—' }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Name</p>
                  <p class="pf-val">{{ previewDoc.name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Type</p>
                  <p class="pf-val">{{ { email: 'Email', sms: 'SMS', push: 'Push Notification' }[previewDoc.type] ?? previewDoc.type }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Recipients</p>
                  <p class="pf-val">{{ { contacts: 'All Contacts', leads: 'All Leads', customers: 'All Customers', employees: 'All Employees', segment: 'Segment', customer_category: 'Customer Category', individual_customer: 'Specific Customer', individual_employee: 'Specific Employee' }[previewDoc.recipient_type] ?? previewDoc.recipient_type ?? '—' }}</p>
                </div>
                <div v-if="previewDoc.from_name || previewDoc.from_email" class="preview-field">
                  <p class="pf-label">From</p>
                  <p class="pf-val">{{ [previewDoc.from_name, previewDoc.from_email].filter(Boolean).join(' · ') }}</p>
                </div>
                <div v-if="previewDoc.subject" class="preview-field">
                  <p class="pf-label">Subject / Title</p>
                  <p class="pf-val">{{ previewDoc.subject }}</p>
                </div>
                <div v-if="previewDoc.scheduled_at" class="preview-field">
                  <p class="pf-label">Scheduled At</p>
                  <p class="pf-val">{{ fmtDate(previewDoc.scheduled_at) }}</p>
                </div>
              </div>
              <div v-if="previewDoc.description" class="preview-reason-box">
                <p class="pf-label">Description</p>
                <p class="pf-reason-text">{{ previewDoc.description }}</p>
              </div>
              <div v-if="previewDoc.body" class="preview-reason-box campaign-body-preview" style="margin-top:8px;">
                <p class="pf-label">Message Body</p>
                <div class="campaign-body-frame" v-html="previewDoc.body"></div>
              </div>
            </template>

            <template v-else-if="previewDoc && previewTarget.module === 'exit_case'">
              <p class="preview-section-title">Exit / Resignation Details</p>
              <div class="preview-grid">
                <div class="preview-field">
                  <p class="pf-label">Employee</p>
                  <p class="pf-val">{{ previewDoc.employee ? previewDoc.employee.first_name + ' ' + previewDoc.employee.last_name : '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Employee No.</p>
                  <p class="pf-val">{{ previewDoc.employee?.employee_no || '—' }}</p>
                </div>
                <div v-if="previewDoc.employee?.department" class="preview-field">
                  <p class="pf-label">Department</p>
                  <p class="pf-val">{{ previewDoc.employee.department.name }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Case Ref.</p>
                  <p class="pf-val"><span class="ref-chip">{{ previewDoc.case_number || '—' }}</span></p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Exit Type</p>
                  <p class="pf-val">{{ { resignation: 'Resignation', termination: 'Termination', retirement: 'Retirement', redundancy: 'Redundancy', contract_end: 'Contract End' }[previewDoc.type] ?? previewDoc.type ?? '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Exit Date</p>
                  <p class="pf-val">{{ previewDoc.exit_date ? fmtDate(previewDoc.exit_date) : '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Last Working Day</p>
                  <p class="pf-val">{{ previewDoc.last_working_day ? fmtDate(previewDoc.last_working_day) : '—' }}</p>
                </div>
                <div class="preview-field">
                  <p class="pf-label">Notice Period</p>
                  <p class="pf-val pf-highlight">{{ previewDoc.notice_period_days != null ? previewDoc.notice_period_days + ' days' : '—' }}</p>
                </div>
              </div>
              <div v-if="previewDoc.reason" class="preview-reason-box">
                <p class="pf-label">Reason</p>
                <p class="pf-reason-text">{{ previewDoc.reason }}</p>
              </div>
            </template>

            <!-- FALLBACK -->
            <div v-else class="preview-no-doc">
              <font-awesome-icon icon="file-alt" class="preview-no-doc-icon" />
              <p>No document details available for this request.</p>
            </div>

          </div>

          <!-- Footer -->
          <div class="modal-ft" v-if="isActable(previewTarget)">
            <button class="btn-ghost" @click="previewTarget = null">Close</button>
            <button class="btn-reject" style="padding:9px 18px;font-size:13px;" @click="previewToAction('reject')">
              <font-awesome-icon icon="times" class="me-1" /> Reject
            </button>
            <button class="btn-approve" style="padding:9px 18px;font-size:13px;" @click="previewToAction('approve')">
              <font-awesome-icon icon="check" class="me-1" /> Approve
            </button>
          </div>
          <div class="modal-ft" v-else>
            <button class="btn-ghost" @click="previewTarget = null">Close</button>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════
           ACTION MODAL (Approve / Reject)
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: !!actionTarget }"
        @click.self="!actioning && (actionTarget = null)">
        <div class="modal-box" v-if="actionTarget">
          <!-- Header -->
          <div class="action-modal-hd" :class="actionTarget.action === 'approve' ? 'action-modal-hd--green' : 'action-modal-hd--red'">
            <div class="action-modal-hd-inner">
              <div class="action-hd-icon">
                <font-awesome-icon :icon="actionTarget.action === 'approve' ? 'check-circle' : 'times-circle'" />
              </div>
              <div>
                <h3 class="action-modal-title">
                  {{ actionTarget.action === 'approve' ? 'Approve Request' : 'Reject Request' }}
                </h3>
                <p class="action-modal-sub">
                  {{ moduleMeta(actionTarget.req.module).label }} · {{ actionTarget.req.module_reference }}
                </p>
              </div>
            </div>
            <button class="modal-close-btn-light" :disabled="actioning"
              @click="actionTarget = null">
              <font-awesome-icon icon="times" />
            </button>
          </div>
  
          <!-- Body -->
          <div class="modal-bd">
            <!-- Request info card -->
            <div class="action-req-info">
              <div class="action-req-module-icon" :style="moduleStyle(actionTarget.req.module)">
                <font-awesome-icon :icon="moduleMeta(actionTarget.req.module).icon" />
              </div>
              <div>
                <p class="action-req-wf">{{ actionTarget.req.workflow && actionTarget.req.workflow.name }}</p>
                <p class="action-req-meta">
                  Submitted by
                  <strong>{{ requesterName(actionTarget.req.requested_by) }}</strong>
                  <span v-if="actionTarget.req.requested_by && actionTarget.req.requested_by.employee && actionTarget.req.requested_by.employee.job_title"
                    class="meta-dim"> · {{ actionTarget.req.requested_by.employee.job_title.name }}</span>
                </p>
                <p v-if="actionTarget.req.notes" class="action-req-notes">"{{ actionTarget.req.notes }}"</p>
              </div>
            </div>
  
            <!-- PO amendment summary (shown when quantities were changed) -->
            <div v-if="actionTarget.action === 'approve' && actionTarget.amendments && Object.keys(actionTarget.amendments).length"
              class="amend-summary-box">
              <font-awesome-icon icon="edit" class="amend-summary-icon" />
              <span>Quantity changes will be saved to <strong>{{ Object.keys(actionTarget.amendments).length }}</strong> line item(s) upon approval.</span>
            </div>

            <!-- Comments -->
            <div class="field-group">
              <label class="field-label-sm">
                Comments
                <span v-if="actionTarget.action === 'reject'" class="req-star">*</span>
                <span v-else class="opt">(optional)</span>
              </label>
              <textarea class="field-input" rows="4" :disabled="actioning"
                :placeholder="actionTarget.action === 'approve'
                  ? 'Add an optional note for the requester...'
                  : 'Please provide a reason for rejection...'"
                v-model="actionComments"></textarea>
            </div>
          </div>
  
          <!-- Footer -->
          <div class="modal-ft">
            <button class="btn-ghost" :disabled="actioning" @click="actionTarget = null">Cancel</button>
            <button class="btn-action-confirm"
              :class="actionTarget.action === 'approve' ? 'btn-action-confirm--green' : 'btn-action-confirm--red'"
              :disabled="actioning"
              @click="handleConfirm">
              <font-awesome-icon v-if="actioning" icon="spinner" class="fa-spin me-1" />
              {{ actionTarget.action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection' }}
            </button>
          </div>
        </div>
      </div>
  
      <!-- ══════════════════════════════
           REASSIGN MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: !!reassignTarget }"
        @click.self="!reassigning && (reassignTarget = null)">
        <div class="modal-box" v-if="reassignTarget">
          <div class="modal-hd">
            <div>
              <p class="modal-ttl"><font-awesome-icon icon="user-edit" class="me-1 text-blue" /> Reassign Approval Step</p>
              <p class="modal-sub">{{ moduleMeta(reassignTarget.module).label }} · {{ reassignTarget.module_reference }}</p>
            </div>
            <button class="modal-close-btn" :disabled="reassigning" @click="reassignTarget = null">
              <font-awesome-icon icon="times" />
            </button>
          </div>

          <div class="modal-bd">
            <!-- Current step info -->
            <div class="reassign-step-info">
              <div class="reassign-step-label">Current active step</div>
              <div class="reassign-step-name">
                Step {{ reassignTarget.current_step }}
                <span v-if="currentStepRecord(reassignTarget)">
                  — {{ currentStepRecord(reassignTarget).position_name || currentStepRecord(reassignTarget).designation?.name }}
                </span>
              </div>
              <div v-if="currentStepAssignee(reassignTarget)" class="reassign-step-assignee">
                Currently assigned to:
                <strong>{{ currentStepAssignee(reassignTarget) }}</strong>
              </div>
            </div>

            <!-- New employee picker -->
            <div class="field-group">
              <label class="field-label-sm">Reassign To <span class="req-star">*</span></label>
              <select class="field-input field-select" v-model="reassignEmployeeId" :disabled="reassigning">
                <option value="">Select employee...</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">
                  {{ e.first_name }} {{ e.last_name }}
                  <template v-if="e.position"> — {{ e.position.name }}</template>
                </option>
              </select>
            </div>

            <!-- Reason -->
            <div class="field-group">
              <label class="field-label-sm">Reason <span class="opt">(optional)</span></label>
              <textarea class="field-input" rows="3" :disabled="reassigning"
                placeholder="Why is this step being reassigned?"
                v-model="reassignReason"></textarea>
            </div>
          </div>

          <div class="modal-ft">
            <button class="btn-ghost" :disabled="reassigning" @click="reassignTarget = null">Cancel</button>
            <button class="btn-dark" :disabled="reassigning || !reassignEmployeeId" @click="handleReassign">
              <font-awesome-icon v-if="reassigning" icon="spinner" class="fa-spin me-1" />
              Reassign Step
            </button>
          </div>
        </div>
      </div>

      <!-- Backdrop -->
      <div v-if="!!actionTarget || !!reassignTarget || !!previewTarget" class="modal-backdrop-custom" />
  
    </div>
  </template>
  
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  
  const MODULE_META = {
    payroll:           { label: 'Payroll',           icon: 'money-bill-wave',   color: '#059669', bg: 'rgba(5,150,105,.1)' },
    leave:             { label: 'Leave',              icon: 'umbrella-beach',    color: '#2563eb', bg: 'rgba(37,99,235,.1)' },
    loan:              { label: 'Loan',               icon: 'hand-holding-usd',  color: '#7c3aed', bg: 'rgba(124,58,237,.1)' },
    expense:           { label: 'Expense',            icon: 'receipt',           color: '#ea580c', bg: 'rgba(234,88,12,.1)' },
    journal:           { label: 'Journal Posting',   icon: 'book',              color: '#004AAD', bg: 'rgba(0,74,173,.1)'  },
    recruitment:       { label: 'Recruitment',        icon: 'user-tie',          color: '#db2777', bg: 'rgba(219,39,119,.1)' },
    'workforce-plan':  { label: 'Workforce Plan',     icon: 'user-tie',          color: '#db2777', bg: 'rgba(219,39,119,.1)' },
    'job-requisition': { label: 'Job Requisition',    icon: 'user-tie',          color: '#db2777', bg: 'rgba(219,39,119,.1)' },
    purchase:                { label: 'Purchase Order',       icon: 'shopping-cart',     color: '#0891b2', bg: 'rgba(8,145,178,.1)'  },
    purchase_requisition:    { label: 'Purchase Requisition', icon: 'file-alt',          color: '#0369a1', bg: 'rgba(3,105,161,.1)'  },
    purchase_order:          { label: 'Procurement PO',       icon: 'shopping-bag',      color: '#0f766e', bg: 'rgba(15,118,110,.1)'  },
    'job-offer':             { label: 'Job Offer',            icon: 'file-signature',    color: '#7c3aed', bg: 'rgba(124,58,237,.1)' },
    'internal-issue':  { label: 'Stock Requisition',  icon: 'boxes',             color: '#0d9488', bg: 'rgba(13,148,136,.1)'  },
    'vendor':          { label: 'Vendor Registration', icon: 'truck',             color: '#b45309', bg: 'rgba(180,83,9,.1)'    },
    'crm_campaign':    { label: 'CRM Campaign',         icon: 'bullhorn',          color: '#7c3aed', bg: 'rgba(124,58,237,.1)'  },
    'exit_case':       { label: 'Exit / Resignation',   icon: 'door-open',         color: '#be185d', bg: 'rgba(190,24,93,.1)'   },
  };
  
  export default {
    name: 'ApprovalInboxPage',
  
    components: {},
  
    data() {
      return {
        MODULE_META,

        loading:      false,
        actioning:    false,

        items:        [],

        search:       '',
        moduleFilter: 'all',
        statusFilter: 'in_progress',
        searchTimer:  null,

        expandedRef:  null,

        // Action modal
        actionTarget:   null,   // { req, action: 'approve'|'reject', amendments: Object|null }
        actionComments: '',

        // PO line qty amendments (keyed by line id)
        amendQtys: {},

        // Reassign modal
        reassignTarget:     null,
        reassignEmployeeId: '',
        reassignReason:     '',
        reassigning:        false,
        employees:          [],

        // Preview modal
        previewTarget:        null,
        previewDoc:           null,
        previewLoading:       false,
        previewRequesterName: null,

        toast: useToast(),
      };
    },
  
    watch: {
      search()      { this.debouncedFetch(); },
      moduleFilter(){ this.fetchItems(); },
      statusFilter(){ this.fetchItems(); },
    },

    computed: {
      permission() { return this.$store.state.getCurrentUserPermission; },

      isSuperAdmin() { return this.$store.state.getLoggedInUser?.is_super == 1; },

      canAmendPO() {
        return this.isSuperAdmin || !!this.permission?.manage_purchase_orders;
      },

      poPreviewLines() {
        return Array.from(this.previewDoc?.lines ?? []);
      },

      filtered() {
        return this.items;
      },
  
      hasActiveFilters() {
        return this.search || this.moduleFilter !== 'all' || this.statusFilter !== 'in_progress';
      },
  
      stats() {
        const all = this.items;
        return {
          total:   all.length,
          pending: all.filter(i => {
            const s = this.reqOf(i).status;
            return s === 'in_progress' || s === 'pending';
          }).length,
          overdue: all.filter(i => this.isOverdue(this.reqOf(i))).length,
        };
      },
    },
  
    created() {
      this.fetchItems();
      this.fetchEmployees();
    },
  
    methods: {
      // -- Data ----------------------------------------------------------
      debouncedFetch() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.fetchItems(), 300);
      },

      fetchItems() {
        this.loading = true;
        const params = {};
        if (this.moduleFilter !== 'all')  params.module = this.moduleFilter;
        if (this.search.trim())           params.search = this.search.trim();

        const isPending = ['in_progress', 'pending'].includes(this.statusFilter);
        params.status = this.statusFilter;
        const endpoint = isPending ? '/approvals/pending' : '/approvals';

        axios.get(endpoint, { params })
          .then(({ data }) => {
            this.items = Array.isArray(data.data?.data) ? data.data.data
              : Array.isArray(data.data) ? data.data : [];
          })
          .catch(() => this.toast.error('Could not load approval requests.'))
          .finally(() => { this.loading = false; });
      },
  
      clearFilters() {
        this.search       = '';
        this.moduleFilter = 'all';
        this.statusFilter = 'in_progress';
        this.fetchItems();
      },
  
      // -- Helpers -------------------------------------------------------
      reqOf(item) {
        return item.approval_request ?? item;
      },
  
      moduleMeta(module) {
        return MODULE_META[module] ?? MODULE_META.payroll;
      },
  
      moduleStyle(module) {
        const m = this.moduleMeta(module);
        return { background: m.bg, color: m.color };
      },
  
      requesterName(req) {
        if (!req) return 'Unknown';
        return req.name || `${req.first_name ?? ''} ${req.last_name ?? ''}`.trim() || 'Unknown';
      },
  
      isActable(req) {
        return req.status === 'in_progress' || req.status === 'pending';
      },
  
      currentStepDue(req) {
        const step = req.steps?.find(s => s.step_order === req.current_step);
        return step?.due_at ?? null;
      },
  
      isOverdue(req) {
        const due = this.currentStepDue(req);
        return due && new Date(due) < new Date();
      },
  
      fmtDate(d) {
        if (!d) return '--';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
      },
  
      fmtTime(d) {
        if (!d) return '';
        return new Date(d).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
      },
  
      statusClass(status) {
        const map = {
          approved:    'status-badge--green',
          rejected:    'status-badge--red',
          in_progress: 'status-badge--blue',
          pending:     'status-badge--amber',
          cancelled:   'status-badge--gray',
        };
        return map[status] ?? 'status-badge--amber';
      },
  
      toggleExpanded(ref) {
        this.expandedRef = this.expandedRef === ref ? null : ref;
      },
  
      // -- Action modal --------------------------------------------------
      openAction(req, action, amendments = null) {
        this.actionTarget   = { req, action, amendments };
        this.actionComments = '';
      },
  
      handleConfirm() {
        if (!this.actionTarget) return;
        const { req, action } = this.actionTarget;

        if (action === 'reject' && !this.actionComments.trim()) {
          this.toast.error('Please provide a reason for rejection.');
          return;
        }

        this.actioning = true;
        const endpoint = `/approvals/${req.reference}/${action}`;

        const payload = { comments: this.actionComments.trim() };
        if (action === 'approve' && this.actionTarget.amendments) {
          payload.line_amendments = Object.entries(this.actionTarget.amendments)
            .map(([id, quantity]) => ({ id, quantity: Number(quantity) }))
            .filter(a => a.quantity > 0);
        }

        axios.post(endpoint, payload)
          .then(() => {
            this.toast.success(action === 'approve' ? 'Request approved successfully.' : 'Request rejected.');
            this.actionTarget = null;
            this.fetchItems();
          })
          .catch(err => this.toast.error(err.response?.data?.message ?? `Failed to ${action} request.`))
          .finally(() => { this.actioning = false; });
      },

      // -- Reassign ----------------------------------------------------------
      fetchEmployees() {
        axios.get('/payroll/employees/active')
          .then(({ data }) => {
            this.employees = Array.isArray(data.data?.data) ? data.data.data
              : Array.isArray(data.data) ? data.data : [];
          })
          .catch(() => {});
      },

      openReassign(req) {
        this.reassignTarget     = req;
        this.reassignEmployeeId = '';
        this.reassignReason     = '';
      },

      currentStepRecord(req) {
        return req.steps?.find(s => s.step_order === req.current_step) ?? null;
      },

      currentStepAssignee(req) {
        const step = this.currentStepRecord(req);
        if (!step) return null;
        if (step.employee_id && step.acted_by_name) return step.acted_by_name;
        return step.position_name || null;
      },

      handleReassign() {
        if (!this.reassignTarget || !this.reassignEmployeeId) return;

        this.reassigning = true;
        const req = this.reassignTarget;

        axios.post(`/approvals/${req.reference}/reassign`, {
          step_order:  req.current_step,
          employee_id: this.reassignEmployeeId,
          reason:      this.reassignReason.trim() || null,
        })
          .then(() => {
            this.toast.success('Step reassigned successfully.');
            this.reassignTarget = null;
            this.fetchItems();
          })
          .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to reassign step.'))
          .finally(() => { this.reassigning = false; });
      },

      setAmendQty(lineId, value) {
        this.amendQtys[lineId] = value === '' ? 0 : Number(value);
      },

      // -- Preview -------------------------------------------------------
      openPreview(req) {
        this.previewTarget        = req;
        this.previewDoc           = null;
        this.previewLoading       = true;
        this.previewRequesterName = null;

        axios.get(`/approvals/${req.reference}/preview`)
          .then(({ data }) => {
            const payload             = data.data ?? {};
            this.previewTarget        = payload.request ?? req;
            this.previewDoc           = payload.document ?? null;
            this.previewRequesterName = payload.requester_name ?? null;

            if (payload.request?.module === 'purchase_order' && payload.document?.lines) {
              const qtys = {};
              payload.document.lines.forEach(l => { qtys[l.id] = l.quantity; });
              this.amendQtys = qtys;
            } else {
              this.amendQtys = {};
            }
          })
          .catch(() => {
            this.toast.error('Could not load document preview.');
            this.previewDoc = null;
          })
          .finally(() => { this.previewLoading = false; });
      },

      previewToAction(action) {
        const req = this.previewTarget;
        const amendments = (action === 'approve' && req?.module === 'purchase_order' && this.canAmendPO)
          ? { ...this.amendQtys }
          : null;
        this.previewTarget = null;
        this.$nextTick(() => this.openAction(req, action, amendments));
      },

      fmtMoney(val) {
        if (val == null) return '—';
        return Number(val).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
  };
  </script>
  
  
  <style scoped>
  /* -- Page ---------------------------------------------------------------- */
  .page-header  { margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; }
  .breadcrumb-link { color:#7a90b0; text-decoration:none; }
  .breadcrumb-link:hover { color:#1a2b45; }
  .breadcrumb-sep { color:#c5d0e0; }
  .me-1 { margin-right:4px; }
  .ms-1 { margin-left:4px; }
  
  /* -- Inbox header card --------------------------------------------------- */
  .inbox-header-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:20px 24px; margin-bottom:20px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; }
  .inbox-header-left  { display:flex; align-items:center; gap:14px; }
  .inbox-shield-icon  { width:48px; height:48px; border-radius:12px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
  .inbox-title        { font-size:18px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .inbox-sub          { font-size:12.5px; color:#7a90b0; margin:0; }
  
  /* -- Stat pills ---------------------------------------------------------- */
  .stat-pills       { display:flex; gap:10px; flex-wrap:wrap; }
  .stat-pill        { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 16px; text-align:center; min-width:64px; }
  .stat-pill--blue  { background:#eff6ff; border-color:#bfdbfe; }
  .stat-pill--red   { background:#fff0f0; border-color:#fecaca; }
  .stat-pill-label  { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 3px; }
  .stat-pill-val    { font-size:18px; font-weight:800; color:#1a2b45; margin:0; }
  .stat-pill-val--blue { color:#2563eb; }
  .stat-pill-val--red  { color:#e03535; }
  
  /* -- Filters ------------------------------------------------------------- */
  .filters-row  { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:18px; }
  .search-wrap  { position:relative; flex:1; min-width:200px; max-width:360px; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { width:100%; padding:8px 12px 8px 34px; border:1px solid #dde5f0; border-radius:9px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:9px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* -- States -------------------------------------------------------------- */
  .state-block    { display:flex; justify-content:center; padding:60px 24px; }
  .empty-state    { display:flex; flex-direction:column; align-items:center; padding:60px 24px; text-align:center; background:#fff; border:2px dashed #e4eaf2; border-radius:14px; }
  .empty-icon-wrap { width:64px; height:64px; border-radius:50%; background:#f0f4f8; display:flex; align-items:center; justify-content:center; font-size:26px; color:#c0c9d8; margin-bottom:14px; }
  .empty-title    { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .empty-sub      { font-size:13px; color:#7a90b0; margin:0; max-width:300px; line-height:1.6; }
  .btn-clear-filters { margin-top:14px; background:none; border:none; color:#004AAD; font-size:13px; font-weight:600; cursor:pointer; text-decoration:underline; }
  
  /* -- Request list -------------------------------------------------------- */
  .request-list  { display:flex; flex-direction:column; gap:12px; }
  .result-count  { font-size:12px; color:#94a3b8; font-weight:500; margin:0 0 4px; }
  
  /* -- Request card -------------------------------------------------------- */
  .request-card         { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; transition:box-shadow .15s; }
  .request-card:hover   { box-shadow:0 4px 20px rgba(0,0,0,.07); }
  .request-card-body    { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; padding:18px 20px; }
  .request-card-left    { display:flex; align-items:flex-start; gap:12px; flex:1; min-width:0; }
  
  /* Module icon */
  .module-icon  { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; margin-top:2px; }
  
  /* Request info */
  .request-info       { flex:1; min-width:0; }
  .request-title-row  { display:flex; flex-wrap:wrap; align-items:center; gap:7px; margin-bottom:5px; }
  .request-name       { font-size:14px; font-weight:700; color:#1a2b45; }
  .request-meta-row   { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin-bottom:8px; }
  .meta-item          { display:flex; align-items:center; gap:4px; font-size:12px; color:#7a90b0; }
  .meta-icon          { font-size:11px; }
  .meta-dim           { color:#94a3b8; }
  .ref-chip           { background:#f0f4f8; color:#64748b; font-size:10.5px; font-weight:600; font-family:monospace; border-radius:5px; padding:2px 7px; }
  .request-notes      { font-size:11.5px; font-style:italic; color:#94a3b8; margin:4px 0 0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:480px; }
  
  /* Status badges */
  .status-badge        { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 9px; text-transform:capitalize; }
  .status-badge--sm    { font-size:10.5px; padding:1px 8px; }
  .status-badge--green { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  .status-badge--red   { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
  .status-badge--blue  { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
  .status-badge--amber { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
  .status-badge--gray  { background:#f4f7fb; color:#64748b; border:1px solid #e4eaf2; }
  
  /* Overdue badge */
  .overdue-badge { display:inline-flex; align-items:center; background:#fff0f0; color:#e03535; font-size:10px; font-weight:700; border-radius:20px; padding:2px 8px; border:1px solid #fecaca; }
  
  /* Step progress dots */
  .step-progress    { display:flex; align-items:center; gap:4px; flex-wrap:wrap; margin-bottom:4px; }
  .step-dot         { width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:9px; font-weight:700; flex-shrink:0; }
  .step-dot--lg     { width:24px; height:24px; font-size:10px; }
  .step-dot--approved { background:#16a34a; color:#fff; }
  .step-dot--rejected { background:#e03535; color:#fff; }
  .step-dot--active   { background:#fff; border:2px solid #004AAD; color:#004AAD; }
  .step-dot--pending  { background:#f0f4f8; color:#94a3b8; }
  .step-label       { font-size:10px; color:#94a3b8; }
  .hidden-sm        { display:none; }
  .step-connector   { height:1px; width:16px; flex-shrink:0; }
  .step-connector--done    { background:#16a34a; }
  .step-connector--pending { background:#e4eaf2; }
  
  /* Right side */
  .request-card-right { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
  .action-btns        { display:flex; gap:6px; }
  .btn-approve  { display:inline-flex; align-items:center; gap:5px; background:#16a34a; color:#fff; font-size:12px; font-weight:700; padding:6px 12px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-approve:hover { background:#15803d; }
  .btn-reject   { display:inline-flex; align-items:center; gap:5px; background:#fff0f0; color:#e03535; font-size:12px; font-weight:700; padding:6px 12px; border-radius:8px; border:1px solid #fecaca; cursor:pointer; transition:all .15s; font-family:inherit; }
  .btn-reject:hover { background:#fecaca; }
  .due-label         { font-size:10.5px; font-weight:500; }
  .due-label--red    { color:#e03535; }
  .due-label--gray   { color:#94a3b8; }
  .btn-toggle-details { display:inline-flex; align-items:center; gap:4px; background:none; border:none; font-size:11px; font-weight:500; color:#7a90b0; cursor:pointer; padding:0; font-family:inherit; }
  .btn-toggle-details:hover { color:#004AAD; }
  .btn-reassign { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; background:#fffbeb; color:#b45309; border:1px solid #fde68a; border-radius:7px; cursor:pointer; font-size:13px; transition:all .15s; font-family:inherit; }
  .btn-reassign:hover { background:#fde68a; }
  
  /* -- Step history (expanded) --------------------------------------------- */
  .step-history         { border-top:1px solid #f0f4f8; background:#f8fafc; padding:16px 20px 20px; }
  .step-history-title   { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#94a3b8; margin:0 0 10px; }
  .step-history-list    { display:flex; flex-direction:column; gap:8px; }
  .step-history-row     { display:flex; align-items:flex-start; gap:12px; border:1px solid #e4eaf2; border-radius:9px; background:#fff; padding:10px 14px; }
  .step-history-row--active { border-color:rgba(0,74,173,.2); background:rgba(0,74,173,.03); }
  .step-history-info    { flex:1; min-width:0; }
  .step-history-title-row { display:flex; align-items:center; gap:8px; margin-bottom:3px; }
  .step-history-name    { font-size:13px; font-weight:600; color:#1a2b45; }
  .step-history-meta    { font-size:11.5px; color:#7a90b0; margin:0 0 2px; }
  .step-history-comment { font-size:11.5px; font-style:italic; color:#7a90b0; margin:2px 0 0; }
  .step-history-due     { font-size:10.5px; margin:2px 0 0; }
  .text-red  { color:#e03535; }
  .text-gray { color:#94a3b8; }
  
  /* -- Action modal -------------------------------------------------------- */
  .modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.5); backdrop-filter:blur(2px); }
  .modal-box          { background:#fff; border-radius:14px; width:100%; max-width:460px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.2); overflow:hidden; transform:translateY(14px); transition:transform .22s ease; z-index:1051; position:relative; max-height:90vh; display:flex; flex-direction:column; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-bd           { padding:20px 24px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; }
  .modal-ft           { padding:14px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }
  
  /* Action modal header */
  .action-modal-hd        { padding:18px 24px; display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-shrink:0; }
  .action-modal-hd--green { background:#16a34a; }
  .action-modal-hd--red   { background:#e03535; }
  .action-modal-hd-inner  { display:flex; align-items:center; gap:12px; }
  .action-hd-icon         { width:36px; height:36px; border-radius:9px; background:rgba(255,255,255,.15); color:#fff; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
  .action-modal-title     { font-size:15px; font-weight:700; color:#fff; margin:0 0 2px; }
  .action-modal-sub       { font-size:11.5px; color:rgba(255,255,255,.7); margin:0; }
  .modal-close-btn-light  { width:30px; height:30px; border-radius:7px; border:none; background:rgba(255,255,255,.15); color:rgba(255,255,255,.8); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
  .modal-close-btn-light:hover { background:rgba(255,255,255,.25); }
  
  /* Action request info card */
  .action-req-info        { display:flex; align-items:flex-start; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:14px; }
  .action-req-module-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; margin-top:2px; }
  .action-req-wf          { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 3px; }
  .action-req-meta        { font-size:12px; color:#7a90b0; margin:0 0 3px; }
  .action-req-notes       { font-size:11.5px; font-style:italic; color:#94a3b8; margin:0; }
  
  /* Fields */
  .field-group    { display:flex; flex-direction:column; gap:4px; }
  .field-label-sm { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; display:flex; align-items:center; gap:5px; }
  .req-star       { color:#e03535; }
  .opt            { font-size:10.5px; color:#94a3b8; font-weight:400; text-transform:none; }
  .field-input    { padding:9px 12px; border:1px solid #dde5f0; border-radius:9px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.07); }
  .field-input:disabled { background:#f4f7fb; }
  
  /* Buttons */
  .btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
  .btn-ghost:disabled { opacity:.45; cursor:not-allowed; }
  .btn-action-confirm { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:700; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; color:#fff; }
  .btn-action-confirm:disabled { opacity:.55; cursor:not-allowed; }
  .btn-action-confirm--green { background:#16a34a; }
  .btn-action-confirm--green:hover:not(:disabled) { background:#15803d; }
  .btn-action-confirm--red { background:#e03535; }
  .btn-action-confirm--red:hover:not(:disabled) { background:#c02828; }
  
  /* -- Reassign modal ------------------------------------------------------ */
  .modal-hd           { padding:18px 24px; border-bottom:1px solid #e4eaf2; display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-shrink:0; }
  .modal-ttl          { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .modal-sub          { font-size:12px; color:#7a90b0; margin:0; }
  .modal-close-btn    { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; flex-shrink:0; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-close-btn:disabled { opacity:.4; cursor:not-allowed; }
  .text-blue          { color:#2563eb; }
  .btn-dark           { display:inline-flex; align-items:center; gap:6px; background:#1a2b45; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
  .btn-dark:hover:not(:disabled) { background:#0f1e33; }
  .btn-dark:disabled  { opacity:.5; cursor:not-allowed; }
  .field-select       { appearance:auto; cursor:pointer; }
  textarea.field-input { resize:vertical; }
  .reassign-step-info   { background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:12px 14px; }
  .reassign-step-label  { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; margin-bottom:4px; }
  .reassign-step-name   { font-size:13.5px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
  .reassign-step-assignee { font-size:12px; color:#64748b; }

  /* -- Preview button & card footer -------------------------------------- */
  .card-footer-btns  { display:flex; align-items:center; gap:8px; }
  .btn-preview       { display:inline-flex; align-items:center; gap:4px; background:none; border:none; font-size:11px; font-weight:600; color:#004AAD; cursor:pointer; padding:0; font-family:inherit; }
  .btn-preview:hover { text-decoration:underline; }

  /* -- Preview modal ------------------------------------------------------ */
  .preview-overlay   { z-index:1060; }
  .preview-box       { max-width:600px; }
  .preview-hd        { padding:18px 24px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-shrink:0; }
  .preview-hd-inner  { display:flex; align-items:center; gap:12px; }
  .preview-hd-icon   { width:36px; height:36px; border-radius:9px; background:rgba(255,255,255,.2); color:#fff; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
  .preview-hd-title  { font-size:15px; font-weight:700; color:#fff; margin:0 0 2px; }
  .preview-hd-sub    { font-size:11.5px; color:rgba(255,255,255,.75); margin:0; font-family:monospace; }
  .preview-loading   { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; gap:12px; }
  .preview-loading-text { font-size:13px; color:#7a90b0; margin:0; }
  .preview-body      { padding:20px 24px; overflow-y:auto; flex:1; min-height:0; display:flex; flex-direction:column; gap:14px; }
  .preview-meta-bar  { display:flex; flex-wrap:wrap; align-items:center; gap:10px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:10px 14px; }
  .preview-meta-item { display:flex; align-items:center; gap:4px; font-size:12px; color:#7a90b0; }
  .preview-notes     { font-size:12px; font-style:italic; color:#94a3b8; margin:0; background:#fefce8; border:1px solid #fde68a; border-radius:8px; padding:8px 12px; }
  .preview-section-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#94a3b8; margin:0; }
  .preview-grid      { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .preview-field     { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px 12px; }
  .pf-label          { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; margin:0 0 3px; }
  .pf-val            { font-size:13px; color:#1a2b45; font-weight:500; margin:0; }
  .pf-highlight      { color:#004AAD; font-weight:700; }
  .preview-reason-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px 12px; }
  .pf-reason-text    { font-size:12.5px; color:#3d5068; margin:4px 0 0; line-height:1.6; }
  .preview-clamp     { display:-webkit-box; -webkit-line-clamp:4; -webkit-box-orient:vertical; overflow:hidden; }
  .leave-type-chip   { display:inline-block; font-size:12px; font-weight:600; padding:2px 9px; border-radius:20px; }
  .preview-attach-link { display:inline-flex; align-items:center; font-size:12.5px; color:#004AAD; font-weight:600; text-decoration:none; }
  .preview-attach-link:hover { text-decoration:underline; }
  .preview-no-doc    { display:flex; flex-direction:column; align-items:center; gap:8px; padding:32px 0; color:#94a3b8; font-size:13px; }
  .preview-no-doc-icon { font-size:36px; }

  /* -- Purchase Requisition preview --------------------------------------- */
  .pr-preview-meta {
    display:grid; grid-template-columns:1fr 1fr; gap:8px;
  }
  .pr-meta-chip {
    background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px;
    padding:9px 12px; display:flex; flex-direction:column; gap:3px;
  }
  .pr-meta-chip--accent {
    background:#eff6ff; border-color:#bfdbfe;
  }
  .pr-meta-label {
    font-size:10px; font-weight:700; text-transform:uppercase;
    letter-spacing:.06em; color:#94a3b8;
  }
  .pr-meta-val  { font-size:13px; font-weight:500; color:#1a2b45; }
  .pr-meta-total { font-size:14px; font-weight:700; color:#004AAD; }

  .pr-lines-wrap {
    border:1px solid #e4eaf2; border-radius:9px; overflow:hidden; flex-shrink:0;
  }
  .pr-lines-head {
    display:flex; align-items:center; gap:8px;
    padding:8px 13px; background:#f8fafc; border-bottom:1px solid #e4eaf2;
    font-size:10px; font-weight:700; text-transform:uppercase;
    letter-spacing:.07em; color:#64748b;
  }
  .pr-lines-count {
    background:#e2e8f0; color:#475569; font-size:10px; font-weight:700;
    border-radius:20px; padding:1px 7px;
  }

  .pr-lines-table {
    width:100%; border-collapse:collapse;
    table-layout:fixed; font-size:12.5px;
  }
  .prc-desc { width:55%; }
  .prc-qty  { width:15%; }
  .prc-cost { width:30%; }

  .pr-lines-table thead tr { background:#1a2b45; }
  .pr-lines-table th {
    padding:8px 12px; font-size:10px; font-weight:700;
    text-transform:uppercase; letter-spacing:.06em;
    color:rgba(255,255,255,.7); text-align:left; border:none;
  }
  .pr-th-r { text-align:right; }

  .pr-lines-table tbody tr     { background:#fff; }
  .pr-lines-table tr.pr-row-alt { background:#f7f9fc; }
  .pr-lines-table tbody tr:not(:last-child) td { border-bottom:1px solid #edf0f5; }
  .pr-lines-table td { padding:9px 12px; vertical-align:middle; }

  .pr-td-desc {
    font-size:12.5px; font-weight:500; color:#1a2b45;
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  }
  .pr-td-r    { text-align:right; }
  .pr-td-qty  {
    font-size:12.5px; font-weight:600; color:#1a2b45;
    font-variant-numeric:tabular-nums;
  }
  .pr-td-cost {
    font-size:12.5px; color:#1a2b45;
    font-variant-numeric:tabular-nums; white-space:nowrap;
  }

  .pr-lines-table tfoot tr   { background:#eff6ff; }
  .pr-lines-table tfoot td   { border-top:2px solid #dbeafe; border-bottom:none; }
  .pr-tfoot-label {
    padding:10px 12px; font-size:10px; font-weight:700;
    text-transform:uppercase; letter-spacing:.06em;
    color:#64748b; text-align:right;
  }
  .pr-tfoot-val {
    padding:10px 12px; font-size:14px; font-weight:800;
    color:#004AAD; text-align:right;
    font-variant-numeric:tabular-nums; white-space:nowrap;
  }

  /* -- Procurement PO preview ---------------------------------------------- */
  .po-vendor-val        { font-weight:700; color:#0f766e; }
  .po-preview-addr-block{ background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px 14px; margin-bottom:12px; }
  .po-preview-addr-label{ font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#64748b; margin:0 0 4px; }
  .po-preview-addr-val  { font-size:13px; color:#1a2b45; margin:0; white-space:pre-line; line-height:1.5; }
  .po-lines-table       { table-layout:auto; }
  .po-line-spec         { display:block; font-size:11px; color:#7a90b0; margin-top:2px; }
  .po-line-uom          { font-size:11px; color:#94a3b8; }
  .po-tfoot-total       { background:#e0f2fe !important; }
  .po-tfoot-total-label { color:#0f766e !important; }
  .po-tfoot-total-val   { color:#0f766e !important; font-size:15px !important; }

  .po-amend-badge { margin-left:10px; font-size:11px; font-weight:600; color:#0f766e; background:#d1fae5; border:1px solid #6ee7b7; border-radius:6px; padding:2px 8px; }
  .amend-qty-input { width:72px; padding:3px 6px; border:1px solid #6ee7b7; border-radius:6px; font-size:13px; font-weight:600; color:#1a2b45; text-align:right; background:#f0fdf4; outline:none; }
  .amend-qty-input:focus { border-color:#0f766e; background:#fff; }
  .amend-summary-box { display:flex; align-items:center; gap:8px; background:#f0fdf4; border:1px solid #6ee7b7; border-radius:8px; padding:10px 14px; margin-bottom:14px; font-size:13px; color:#166534; }
  .amend-summary-icon { color:#0f766e; flex-shrink:0; }

  /* -- Journal entries table ---------------------------------------------- */
  .jnl-ref-chip       { font-family:monospace; font-size:12px; font-weight:700; background:#eff6ff; color:#004AAD; border:1px solid #bfdbfe; border-radius:5px; padding:2px 8px; }
  .jnl-entries-table  { border:1px solid #e4eaf2; border-radius:9px; overflow:hidden; font-size:12.5px; }
  .jnl-entries-head   { display:grid; grid-template-columns:1fr 130px 130px; background:#f8fafc; padding:8px 14px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; border-bottom:1px solid #e4eaf2; }
  .jnl-entries-row    { display:grid; grid-template-columns:1fr 130px 130px; padding:9px 14px; border-bottom:1px solid #f0f4f8; color:#1a2b45; }
  .jnl-entries-row:last-child { border-bottom:none; }
  .jnl-entries-total  { display:grid; grid-template-columns:1fr 130px 130px; padding:9px 14px; background:#f8fafc; border-top:1px solid #e4eaf2; font-weight:700; color:#1a2b45; font-size:12.5px; }
  .jnl-account-name   { font-weight:500; }
  .jnl-debit          { color:#004AAD; font-weight:600; }
  .jnl-credit         { color:#16a34a; font-weight:600; }
  .text-right         { text-align:right; }

  /* -- CRM Campaign body preview ------------------------------------------- */
  .campaign-body-preview { padding:0; background:none; border:none; }
  .campaign-body-frame {
    border:1px solid #e4eaf2; border-radius:8px;
    background:#fff; padding:20px 24px;
    font-family:'Helvetica Neue', Arial, sans-serif;
    font-size:14px; line-height:1.7; color:#1a2b45;
    max-height:420px; overflow-y:auto;
  }

  /* -- Vendor preview ------------------------------------------------------ */
  .vendor-cats      { display:flex; flex-wrap:wrap; gap:6px; }
  .vendor-cat-chip  { background:#fff7ed; color:#b45309; border:1px solid #fed7aa; border-radius:20px; font-size:11.5px; font-weight:600; padding:2px 10px; }
  .pf-val--red      { color:#e03535; font-weight:600; }
  .pf-val--amber    { color:#b45309; font-weight:600; }

  /* -- Responsive ---------------------------------------------------------- */
  @media (min-width:640px) { .hidden-sm { display:inline; } }
  @media (max-width:640px) {
    .inbox-header-card  { flex-direction:column; }
    .request-card-body  { flex-direction:column; }
    .request-card-right { align-items:flex-start; flex-direction:row; flex-wrap:wrap; }
    .preview-grid { grid-template-columns:1fr; }
  }
  </style>

<style>
/* Non-scoped: Quill HTML content rendering inside .campaign-body-frame (v-html bypasses scoped attributes) */
.campaign-body-frame { box-sizing: border-box; }
.campaign-body-frame p,
.campaign-body-frame ol,
.campaign-body-frame ul,
.campaign-body-frame pre,
.campaign-body-frame blockquote,
.campaign-body-frame h1,
.campaign-body-frame h2,
.campaign-body-frame h3,
.campaign-body-frame h4,
.campaign-body-frame h5,
.campaign-body-frame h6 { margin: 0; padding: 0; }
.campaign-body-frame p   { margin-bottom: 10px; }
.campaign-body-frame h1  { font-size: 2em;    font-weight: 700; line-height: 1.3; margin-bottom: 8px; }
.campaign-body-frame h2  { font-size: 1.5em;  font-weight: 700; line-height: 1.3; margin-bottom: 6px; }
.campaign-body-frame h3  { font-size: 1.17em; font-weight: 700; line-height: 1.3; margin-bottom: 6px; }
.campaign-body-frame strong { font-weight: 700; }
.campaign-body-frame em     { font-style: italic; }
.campaign-body-frame u      { text-decoration: underline; }
.campaign-body-frame s      { text-decoration: line-through; }
.campaign-body-frame a      { color: #004AAD; text-decoration: underline; }
.campaign-body-frame blockquote { border-left: 4px solid #ccc; padding-left: 16px; margin: 5px 0; color: #5a6a85; }
.campaign-body-frame pre    { background: #23241f; color: #f8f8f2; white-space: pre-wrap; padding: 5px 10px; border-radius: 3px; margin: 5px 0; }
.campaign-body-frame img    { max-width: 100%; height: auto; display: block; border-radius: 4px; margin: 4px 0; }
.campaign-body-frame table  { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
.campaign-body-frame td,
.campaign-body-frame th     { padding: 6px 10px; border: 1px solid #e4eaf2; }
.campaign-body-frame th     { background: #f4f7fb; font-weight: 600; }
/* Lists — restore native rendering overridden by app reset */
.campaign-body-frame ol,
.campaign-body-frame ul     { padding-left: 1.5em; margin-bottom: 10px; }
.campaign-body-frame ul     { list-style-type: disc; }
.campaign-body-frame ol     { list-style-type: decimal; }
.campaign-body-frame li     { list-style: inherit; margin-bottom: 3px; padding-left: 0; }
.campaign-body-frame ul ul  { list-style-type: circle; }
.campaign-body-frame ul ul ul { list-style-type: square; }
/* Quill indentation */
.campaign-body-frame .ql-indent-1 { padding-left: 3em; }
.campaign-body-frame .ql-indent-2 { padding-left: 4.5em; }
.campaign-body-frame .ql-indent-3 { padding-left: 6em; }
.campaign-body-frame .ql-indent-4 { padding-left: 7.5em; }
/* Quill alignment */
.campaign-body-frame .ql-align-center  { text-align: center; }
.campaign-body-frame .ql-align-right   { text-align: right; }
.campaign-body-frame .ql-align-justify { text-align: justify; }
</style>